/**
 * EL ARBOL COMO MAPA -- #/arbol (ARBOL-GRAFO-VISTA-T1, Josse 3-nov, Ares #42417/#42418). PR 1 de 3: MAPA.
 *
 * AUDIENCIA: Josse o un admin de Nexo, en el panel, sobre todo en escritorio (en el celular se puede mover y ampliar).
 * Responde: como esta hecho un proyecto por dentro, que vive, que falta y que no se debe usar, de un vistazo.
 *
 * El proyecto en el centro, sus grupos en un anillo y los nodos como hojas. Color por estado, tamano por cuantos lo usan.
 * Se mueve arrastrando y se amplia con la rueda o los botones; un clic en un nodo abre su ficha corta. Las posiciones las
 * calcula application/arbol/mapaDelArbol.js (sin DOM, lo prueba check-arbol-mapa). Los datos salen de la ruta de A
 * (API 1.6.1009); si falla, se dice la causa y no se pinta nada. Las vistas RECORRIDO y API llegan en los PR 2 y 3.
 *
 * @module presentation/views/ArbolView
 */
import { ArbolApiClient } from '../../infrastructure/api/ArbolApiClient.js';
import { leerArbol, disposicionRadial, conteoPorEstado, fraseDeLaFalla, ESTADOS } from '../../application/arbol/mapaDelArbol.js';
import { esc } from '../shared/texto.js';
import { regresoHtml } from '../shared/regreso.js';

const PROYECTO = 'panel-v2';

/** Color y nombre de cada estado (la leyenda dice cifras, no solo colores). */
export const ESTADO_VISTA = {
  vive: { color: 'var(--accent-green-text)', nombre: 'vive' },
  abierto: { color: 'var(--accent-yellow)', nombre: 'abierto' },
  falta: { color: 'var(--accent-red)', nombre: 'falta' },
  no_usar: { color: 'var(--text-muted)', nombre: 'no usar' }
};

const ESTILO = {
  caja: 'background:var(--bg-card);border:1px solid var(--border-default);border-radius:var(--radius-md);padding:12px 16px;margin-top:12px;color:var(--text-primary)',
  suave: 'color:var(--text-secondary);font-size:0.875rem',
  lienzo: 'display:block;width:100%;height:min(70vh,640px);background:var(--bg-canvas);border:1px solid var(--border-default);border-radius:var(--radius-md);touch-action:none;cursor:grab',
  boton: 'padding:6px 12px;border-radius:var(--radius-sm);border:1px solid var(--border-default);background:var(--bg-tertiary);color:var(--text-primary);cursor:pointer;font-size:0.875rem;min-width:40px;min-height:36px'
};

/** La leyenda: un punto de color, el estado y cuantos hay. */
export function leyendaHtml(conteo) {
  return `<div data-arbol-leyenda style="display:flex;flex-wrap:wrap;gap:12px;align-items:center">${ESTADOS.map((e) => `<span data-arbol-estado="${e}" style="display:inline-flex;gap:6px;align-items:center;${ESTILO.suave}"><span style="width:10px;height:10px;border-radius:50%;background:${ESTADO_VISTA[e].color}"></span>${esc(ESTADO_VISTA[e].nombre)} <b style="color:var(--text-strong)">${conteo[e] ?? 0}</b></span>`).join('')}</div>`;
}

/** El dibujo entero, puro sobre el arbol y sus posiciones (para el guardia). */
export function mapaSvg(arbol, pos) {
  const ext = pos.extension;
  const ramas = pos.grupos.map((g) => `<line x1="0" y1="0" x2="${g.x}" y2="${g.y}" style="stroke:var(--border-default);stroke-width:2"></line>`).join('');
  const grupoDe = Object.fromEntries(pos.grupos.map((g) => [g.id, g]));
  const hojas = arbol.nodos.map((n) => {
    const p = pos.nodos[n.id];
    const g = grupoDe[n.grupo];
    return `<line x1="${g.x}" y1="${g.y}" x2="${p.x}" y2="${p.y}" style="stroke:var(--border-muted);stroke-width:1"></line>`;
  }).join('');
  const nodos = arbol.nodos.map((n) => {
    const p = pos.nodos[n.id];
    return `<g data-arbol-nodo="${esc(n.id)}" data-estado="${n.estado}" tabindex="0" role="button" aria-label="${esc(n.ref)}, ${esc(ESTADO_VISTA[n.estado].nombre)}" style="cursor:pointer"><title>${esc(n.ref)} (${esc(ESTADO_VISTA[n.estado].nombre)}, ${n.usos} usos)</title><circle cx="${p.x}" cy="${p.y}" r="${p.r}" style="fill:${ESTADO_VISTA[n.estado].color};stroke:var(--bg-canvas);stroke-width:1.5"></circle></g>`;
  }).join('');
  const grupos = pos.grupos.map((g) => `<g data-arbol-grupo="${esc(g.id)}"><circle cx="${g.x}" cy="${g.y}" r="14" style="fill:var(--bg-tertiary);stroke:var(--accent-blue);stroke-width:2"></circle><text x="${g.x}" y="${g.y - 20}" text-anchor="middle" style="fill:var(--text-strong);font-size:14px;font-weight:600;paint-order:stroke;stroke:var(--bg-canvas);stroke-width:4">${esc(g.nombre)}</text></g>`).join('');
  const centro = `<g data-arbol-centro><circle cx="0" cy="0" r="22" style="fill:var(--accent-blue-strong)"></circle><text x="0" y="40" text-anchor="middle" style="fill:var(--text-bright);font-size:15px;font-weight:700;paint-order:stroke;stroke:var(--bg-canvas);stroke-width:4">${esc(arbol.proyecto.nombre || arbol.proyecto.key)}</text></g>`;
  return `<svg data-arbol-lienzo viewBox="${-ext} ${-ext} ${2 * ext} ${2 * ext}" preserveAspectRatio="xMidYMid meet" style="${ESTILO.lienzo}" role="img" aria-label="Mapa del arbol">${ramas}${hojas}${grupos}${nodos}${centro}</svg>`;
}

/** La ficha corta de un nodo: que hace, quien lo usa, version y su prueba. */
export function fichaHtml(n) {
  if (!n) return `<p style="${ESTILO.suave};margin:0">Toca un nodo para ver su ficha.</p>`;
  const fila = (k, v) => `<div style="display:flex;gap:8px;padding:4px 0;border-top:1px solid var(--border-muted)"><span style="${ESTILO.suave};min-width:96px">${k}</span><span>${v ? esc(v) : `<span style="${ESTILO.suave}">sin dato</span>`}</span></div>`;
  return `<div data-arbol-ficha="${esc(n.id)}"><h2 style="font-size:1rem;margin:0 0 6px;color:var(--text-strong);overflow-wrap:anywhere">${esc(n.ref)}</h2>
    <p style="margin:0 0 6px;display:flex;gap:6px;align-items:center;${ESTILO.suave}"><span style="width:10px;height:10px;border-radius:50%;background:${ESTADO_VISTA[n.estado].color}"></span>${esc(ESTADO_VISTA[n.estado].nombre)} · ${n.usos} usos · ${esc(n.tipo || 'sin tipo')}</p>
    ${fila('Que hace', n.queHace)}${fila('Quien lo usa', n.quienLoUsa)}${fila('Version', n.version)}${fila('Prueba', n.prueba)}</div>`;
}

/** Ampliar o reducir el viewBox alrededor de un punto (x,y en coordenadas del dibujo). */
function ampliar(vb, factor, x = vb.x + vb.w / 2, y = vb.y + vb.h / 2) {
  const w = Math.min(vb.max, Math.max(vb.min, vb.w * factor));
  const k = w / vb.w;
  return { ...vb, x: x - (x - vb.x) * k, y: y - (y - vb.y) * k, w, h: vb.h * k };
}

export async function renderArbol(container) {
  if (!container) return;
  const cabeza = `${regresoHtml()}<h1 style="margin:8px 0 4px">Arbol del proyecto</h1><p style="${ESTILO.suave};margin:0 0 12px">El proyecto en el centro, sus grupos alrededor y cada pieza como una hoja. Arrastra para moverte; rueda o botones para ampliar.</p>`;
  container.innerHTML = `${cabeza}<div data-arbol-cuerpo aria-busy="true"><p style="${ESTILO.suave}">Cargando...</p></div>`;
  const res = await ArbolApiClient.leer(PROYECTO);
  const cuerpo = container.querySelector('[data-arbol-cuerpo]');
  if (!cuerpo) return;
  // A sirvio la ruta (1.6.1009, #42809): sin ejemplo; si falla, se dice la causa y no se pinta nada inventado.
  const data = res.ok ? res.data : null;
  cuerpo.removeAttribute('aria-busy');
  if (!res.ok) {
    cuerpo.innerHTML = `<p data-arbol-error style="color:var(--accent-red)">${esc(fraseDeLaFalla(res.motivo))}</p>`;
    return;
  }
  const arbol = leerArbol(data);
  if (!arbol) {
    cuerpo.innerHTML = `<p data-arbol-vacio style="${ESTILO.suave}">Este proyecto todavia no tiene nodos en el arbol.</p>`;
    return;
  }
  const pos = disposicionRadial(arbol);
  const controles = `<div style="display:flex;gap:6px;align-items:center;justify-content:space-between;flex-wrap:wrap;margin:0 0 8px">${leyendaHtml(conteoPorEstado(arbol))}<span style="display:flex;gap:6px"><button type="button" data-arbol-zoom="mas" aria-label="Ampliar" style="${ESTILO.boton}">+</button><button type="button" data-arbol-zoom="menos" aria-label="Reducir" style="${ESTILO.boton}">-</button><button type="button" data-arbol-zoom="todo" style="${ESTILO.boton}">Ver todo</button></span></div>`;
  cuerpo.innerHTML = `${controles}${mapaSvg(arbol, pos)}<section data-arbol-ficha-caja style="${ESTILO.caja}">${fichaHtml(null)}</section>`;

  const svg = cuerpo.querySelector('[data-arbol-lienzo]');
  const ficha = cuerpo.querySelector('[data-arbol-ficha-caja]');
  const ext = pos.extension;
  const inicial = { x: -ext, y: -ext, w: 2 * ext, h: 2 * ext, min: 2 * ext / 12, max: 2 * ext * 1.5 };
  let vb = { ...inicial };
  const aplicar = () => svg.setAttribute('viewBox', `${vb.x} ${vb.y} ${vb.w} ${vb.h}`);
  // Pixeles de pantalla -> coordenadas del dibujo (respeta el "meet" del preserveAspectRatio).
  const aDibujo = (cx, cy) => {
    const m = svg.getScreenCTM();
    if (!m) return { x: vb.x + vb.w / 2, y: vb.y + vb.h / 2 };
    const p = new DOMPoint(cx, cy).matrixTransform(m.inverse());
    return { x: p.x, y: p.y };
  };
  const porId = Object.fromEntries(arbol.nodos.map((n) => [n.id, n]));
  const abrir = (id) => {
    ficha.innerHTML = fichaHtml(porId[id]);
    svg.querySelectorAll('[data-arbol-nodo] circle').forEach((c) => { c.style.stroke = 'var(--bg-canvas)'; c.style.strokeWidth = '1.5'; });
    const c = svg.querySelector(`[data-arbol-nodo="${CSS.escape(id)}"] circle`);
    if (c) { c.style.stroke = 'var(--text-bright)'; c.style.strokeWidth = '3'; }
  };

  cuerpo.querySelectorAll('[data-arbol-zoom]').forEach((b) => b.addEventListener('click', () => {
    const que = b.getAttribute('data-arbol-zoom');
    vb = que === 'todo' ? { ...inicial } : ampliar(vb, que === 'mas' ? 0.7 : 1 / 0.7);
    aplicar();
  }));
  svg.addEventListener('wheel', (e) => {
    e.preventDefault();
    const p = aDibujo(e.clientX, e.clientY);
    vb = ampliar(vb, e.deltaY < 0 ? 0.85 : 1 / 0.85, p.x, p.y);
    aplicar();
  }, { passive: false });

  // Arrastrar mueve el mapa; un toque sin moverse (menos de 4 px) es un clic sobre el nodo. Dos dedos: pellizco.
  let arrastre = null;
  let pellizco = null;
  const dedos = new Map();
  const separacion = () => { const [a, b] = [...dedos.values()]; return Math.hypot(a.x - b.x, a.y - b.y) || 1; };
  svg.addEventListener('pointerdown', (e) => {
    dedos.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (dedos.size === 2) {
      // El segundo dedo cancela el arrastre (y el clic) y empieza el pellizco alrededor del punto medio.
      const [a, b] = [...dedos.values()];
      arrastre = null;
      pellizco = { d: separacion(), vb: { ...vb }, medio: aDibujo((a.x + b.x) / 2, (a.y + b.y) / 2) };
      return;
    }
    arrastre = { px: e.clientX, py: e.clientY, desde: aDibujo(e.clientX, e.clientY), vb: { ...vb }, movio: false, id: e.pointerId };
    svg.style.cursor = 'grabbing';
  });
  svg.addEventListener('pointermove', (e) => {
    if (dedos.has(e.pointerId)) dedos.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pellizco && dedos.size === 2) {
      vb = ampliar(pellizco.vb, pellizco.d / separacion(), pellizco.medio.x, pellizco.medio.y);
      aplicar();
      return;
    }
    if (!arrastre || e.pointerId !== arrastre.id) return;
    if (!arrastre.movio && Math.hypot(e.clientX - arrastre.px, e.clientY - arrastre.py) < 4) return;
    if (!arrastre.movio) {
      arrastre.movio = true;
      try { svg.setPointerCapture(e.pointerId); } catch { /* sin captura el arrastre sigue mientras el puntero este encima */ }
    }
    vb = arrastre.vb;
    aplicar();
    const ahora = aDibujo(e.clientX, e.clientY);
    vb = { ...arrastre.vb, x: arrastre.vb.x - (ahora.x - arrastre.desde.x), y: arrastre.vb.y - (ahora.y - arrastre.desde.y) };
    aplicar();
  });
  const soltar = (e) => {
    dedos.delete(e.pointerId);
    if (dedos.size < 2) pellizco = null;
    if (!arrastre) return;
    const fueClic = !arrastre.movio;
    arrastre = null;
    svg.style.cursor = 'grab';
    if (!fueClic) return;
    const g = e.target.closest?.('[data-arbol-nodo]');
    if (g) abrir(g.getAttribute('data-arbol-nodo'));
  };
  svg.addEventListener('pointerup', soltar);
  svg.addEventListener('pointercancel', (e) => { dedos.delete(e.pointerId); pellizco = null; arrastre = null; svg.style.cursor = 'grab'; });
  svg.addEventListener('keydown', (e) => {
    const g = e.target.closest?.('[data-arbol-nodo]');
    if (g && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); abrir(g.getAttribute('data-arbol-nodo')); }
  });
}
